/**
 * Car usage (was the Matcher Master, now automatic - no uploads): every car of a branch over a period -
 * who had it when, days rented, usage %, how long it has stood idle, cars with several contracts.
 * Built by the engine from the contract periods (E-Check log + contract files): /api/reports/car-usage.
 */
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import Layout from '../../components/Layout';
import Button from '../../ui/Button';
import Badge from '../../ui/Badge';
import Icon from '../../ui/Icon';
import { ICONS } from '../../ui/icon-map';
import EmptyState from '../../ui/EmptyState';
import { Input } from '../../ui/Field/Field';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../ui/Table/Table';
import { CellId, CellMuted, CellPlate } from '../../ui/DataTable/cells';
import LoadingSpinner from '../../components/LoadingSpinner';
import { finesService, errorMessage } from '../../services/finesService';
import { contactsService, formatPhone, phoneDigits } from '../../services/contactsService';
import { Notice } from '../OperationsPortal/components/collectionsShared';
import { Segmented, Card, useFillHeight } from '../BranchCharges/BranchCharges';
import { PRESETS, periodOf } from '../BranchCharges/branchCharges.logic';
import { formatContractDateTime } from '../../utils/contractDateUtils';
import { contractDateSheetCell, fitContractDateColumns } from '../../utils/excelDates';
import { USE_CONTRACTS_V2 } from '../../services/contractsV2';
import { usePermissions } from '../../hooks/usePermissions';

const BRANCHES = [['T1', 'T1'], ['T2', 'T2'], ['BB', 'B.B'], ['INV', 'INV'], ['all', 'All']];
const PREF = 'carUsage.prefs';
const loadPrefs = () => { try { return JSON.parse(localStorage.getItem(PREF) || '{}'); } catch { return {}; } };
// a card shows a part of the cars
const VIEW = {
    all: () => true, rented: (c) => c.contracts > 0, idle: (c) => c.contracts === 0,
    repeated: (c) => c.contracts > 1, now: (c) => c.rentedNow,
};
const carName = (c) => [c.make, c.model, c.year].filter(Boolean).join(' ');
const phoneOf = (contract) => formatPhone(phoneDigits(contract && contactsService.peek(contract)?.p));   // "+966540550044"
// a contract's pick-up / return as shown: the engine's "2026-09-01 10:00" -> "01/09/2026 10:00" (owner, 27 Sep 2026)
const when = (v) => formatContractDateTime(v) || v || '';

export default function CarUsage() {
    // the Excel carries names and phones: its own permission (permissions phase 4, 4 Oct 2026)
    const canExport = usePermissions().hasPermission('export_car_usage');
    const saved = loadPrefs();
    const [branch, setBranch] = useState(saved.branch || 'T1');
    const [preset, setPreset] = useState(saved.preset || 'month');
    const [range, setRange] = useState(() => (saved.preset === 'custom' && saved.from ? { from: saved.from, to: saved.to } : periodOf(saved.preset || 'month')));
    const [report, setReport] = useState(null);
    const [busy, setBusy] = useState('');
    const [err, setErr] = useState('');
    const [view, setView] = useState('all');
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState({});
    const [phones, setPhones] = useState(contactsService.isLoaded());

    useEffect(() => { contactsService.ensureLoaded().then(() => setPhones(true)).catch(() => { /* phones stay hidden */ }); }, []);
    // the contracts store (USE_CONTRACTS_V2) keeps no copy of every contract in the browser: the phones of the
    // report's contracts are read by number, then shown
    const [, setPhonesRead] = useState(0);
    useEffect(() => {
        if (!USE_CONTRACTS_V2 || !report) return undefined;
        let alive = true;
        const nos = (report.cars || []).flatMap((c) => [...(c.periods || []).map((p) => p.contract), c.last?.contract]).filter(Boolean);
        contactsService.prefetch(nos).then(() => { if (alive) setPhonesRead((n) => n + 1); });
        return () => { alive = false; };
    }, [report]);
    useEffect(() => {
        try { localStorage.setItem(PREF, JSON.stringify({ branch, preset, ...(preset === 'custom' ? range : {}) })); } catch { /* storage blocked */ }
    }, [branch, preset, range]);
    const pickPreset = (id) => { setPreset(id); if (id !== 'custom') setRange(periodOf(id)); };

    const loadSeq = useRef(0);   // only the latest answer lands: T1 then T2 clicked fast never shows T1 as T2
    const load = async () => {
        if (!range.from || !range.to) return;
        const seq = ++loadSeq.current;
        setBusy('Reading the cars…'); setErr('');
        try {
            const got = await finesService.carUsage({ branch, from: range.from, to: range.to });
            if (seq === loadSeq.current) { setReport(got); setOpen({}); }
        } catch (e) { if (seq === loadSeq.current) { setErr(errorMessage(e)); setReport(null); } }
        finally { if (seq === loadSeq.current) setBusy(''); }
    };
    useEffect(() => { load(); }, [branch, range.from, range.to]);   // eslint-disable-line react-hooks/exhaustive-deps

    const cars = useMemo(() => {
        const words = search.toLowerCase().split(/\s+/).filter(Boolean);
        return (report?.cars || []).filter(VIEW[view] || VIEW.all).filter((c) => {
            if (!words.length) return true;
            const hay = `${c.plate} ${c.plate.replace(/\s+/g, '')} ${carName(c)} ${c.periods.map((p) => `${p.contract} ${p.customer}`).join(' ')} ${c.last?.customer || ''}`.toLowerCase();
            return words.every((w) => hay.includes(w));
        });
    }, [report, view, search]);
    // idle cars: the longest idle first
    const shown = useMemo(() => (view === 'idle' ? [...cars].sort((a, b) => (b.idleDays ?? 9999) - (a.idleDays ?? 9999)) : cars), [cars, view]);
    const s = report?.summary;
    const branchText = BRANCHES.find(([id]) => id === branch)?.[1] || branch;
    const choose = (id) => setView((v) => (v === id ? 'all' : id));
    const [tableRef, tableH] = useFillHeight(`${!!s}-${err}-${preset}`);

    // the contract dates are real Excel dates shown dd/mm/yyyy hh:mm: they sort and filter by date in Excel
    const excel = () => {
        const book = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(book, fitContractDateColumns(XLSX.utils.json_to_sheet(shown.map((c) => ({
            Plate: c.plate, Car: carName(c), Branch: c.branch, Status: c.status, Invygo: c.invygo ? 'Yes' : '',
            Contracts: c.contracts, 'Days rented': c.rentedDays, 'Usage %': c.utilization, 'Rented now': c.rentedNow ? 'Yes' : '',
            'Last contract': c.last?.contract || '', 'Last customer': c.last?.customer || '', Phone: phoneOf(c.last?.contract),
            'Last return': c.last?.end ? contractDateSheetCell(c.last.end) : (c.rentedNow ? 'Open' : ''), 'Idle days': c.idleDays ?? '',
        })))), 'Cars');
        XLSX.utils.book_append_sheet(book, fitContractDateColumns(XLSX.utils.json_to_sheet(shown.flatMap((c) => c.periods.map((p) => ({
            Plate: c.plate, Car: carName(c), Contract: p.contract, Customer: p.customer, Phone: phoneOf(p.contract),
            Branch: p.branch, From: contractDateSheetCell(p.start), To: p.isOpen ? 'Open' : contractDateSheetCell(p.end),
        }))))), 'Periods');
        XLSX.writeFile(book, `Car_Usage_${branch}_${range.from}_${range.to}.xlsx`);
    };

    return (
        <Layout title="Car usage" subtitle="Who had each car, days rented, idle cars and cars with several contracts - automatic, no uploads">
            <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-3">
                    <Segmented value={branch} onChange={setBranch} options={BRANCHES} label="Branch" />
                    <Segmented value={preset} onChange={pickPreset} options={PRESETS.map((p) => [p.id, p.label])} label="Period" />
                    {preset === 'custom' && (
                        <span className="inline-flex items-center gap-2">
                            <Input type="date" size="sm" aria-label="From" value={range.from} max={range.to || undefined} onChange={(e) => setRange({ ...range, from: e.target.value })} />
                            <span className="text-gray-500">→</span>
                            <Input type="date" size="sm" aria-label="To" value={range.to} min={range.from || undefined} onChange={(e) => setRange({ ...range, to: e.target.value })} />
                        </span>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-gray-700 whitespace-nowrap">
                        <b>{branchText}</b> · {range.from}{range.to !== range.from ? ` → ${range.to}` : ''}{report?.builtAt ? ` · built ${report.builtAt.slice(11, 16)}` : ''}
                    </span>
                    {busy && <span className="inline-flex items-center gap-1.5 text-sm text-gray-600" role="status"><LoadingSpinner size="sm" />{busy}</span>}
                    <div className="flex-1 min-w-[220px] max-w-[420px] ml-auto">
                        <div className="ds-search" style={{ flex: 'none', width: '100%' }}>
                            <Icon glyph={ICONS.search} size="sm" />
                            <input type="search" aria-label="Search cars" placeholder="Search a plate, car, contract or customer…" value={search} onChange={(e) => setSearch(e.target.value)} />
                        </div>
                    </div>
                    <Button size="sm" onClick={load} disabled={!!busy} leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />}>Refresh</Button>
                    {canExport && <Button size="sm" variant="primary" onClick={excel} disabled={!!busy || !shown.length} leadingIcon={<Icon glyph={ICONS.download} size="sm" />}>Excel</Button>}
                </div>

                {err && <Notice tone="danger">{err}</Notice>}

                {s && (
                    <div className="flex flex-wrap gap-2">
                        <Card label="Cars" value={s.cars} sub="in the fleet (RTA, not sold)" active={view === 'all'} onClick={() => setView('all')} />
                        <Card label="Rented in the period" value={s.rented} sub="at least one contract" active={view === 'rented'} onClick={() => choose('rented')} />
                        <Card label="Idle" value={s.idle} sub="no contract in the period" tone={s.idle ? 'text-amber-700' : ''} active={view === 'idle'} onClick={() => choose('idle')} />
                        <Card label="2+ contracts" value={s.repeated} sub="repeated cars" active={view === 'repeated'} onClick={() => choose('repeated')} />
                        <Card label="Rented now" value={s.rentedNow} sub="open contract" active={view === 'now'} onClick={() => choose('now')} />
                        <Card label="Average usage" value={`${s.utilization}%`} sub="days rented / days of the period" />
                    </div>
                )}

                <div ref={tableRef}>
                    <TableContainer maxHeight={`${tableH}px`} className="overflow-auto">
                        <Table sticky className="w-full">
                            <TableHead>
                                <TableRow>
                                    {['#', 'Plate', 'Car', 'Branch', 'Status', 'Contracts', 'Days rented', 'Usage', 'Last contract', 'Idle days', ''].map((h, i) => (
                                        <TableHeader key={i} className="whitespace-nowrap">{h || <span className="sr-only">Periods</span>}</TableHeader>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {shown.map((c, i) => (
                                    <Fragment key={c.plate}>
                                        <TableRow className="cursor-pointer" onClick={() => setOpen((o) => ({ ...o, [c.plate]: !o[c.plate] }))}>
                                            <TableCell>{i + 1}</TableCell>
                                            <TableCell><CellPlate>{c.plate}</CellPlate></TableCell>
                                            <TableCell>{carName(c) || '—'}{c.invygo && <Badge size="sm" variant="accent" className="ml-1">Invygo</Badge>}</TableCell>
                                            <TableCell><Badge size="sm" variant="neutral">{c.branch || '—'}</Badge></TableCell>
                                            <TableCell><span className="text-xs">{c.status || '—'}</span></TableCell>
                                            <TableCell className="ds-cell-num font-semibold">{c.contracts || <span className="text-gray-400">0</span>}</TableCell>
                                            <TableCell className="ds-cell-num">{c.rentedDays}</TableCell>
                                            <TableCell>
                                                <span className="inline-flex items-center gap-1.5">
                                                    <span className="w-16 h-1.5 rounded-full bg-gray-200 overflow-hidden"><span className="block h-full bg-purple-600" style={{ width: `${c.utilization}%` }} /></span>
                                                    <span className="text-xs tabular-nums">{c.utilization}%</span>
                                                </span>
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap">
                                                {c.last ? (
                                                    <>
                                                        <CellId>{c.last.contract}</CellId>
                                                        <CellMuted>{c.last.customer}{phones && phoneOf(c.last.contract) ? ` · ${phoneOf(c.last.contract)}` : ''}</CellMuted>
                                                    </>
                                                ) : <span className="text-gray-400">no contract on record</span>}
                                            </TableCell>
                                            <TableCell className={`ds-cell-num ${c.idleDays >= 7 ? 'text-amber-700 font-semibold' : ''}`}>
                                                {c.rentedNow ? <Badge size="sm" variant="info">Rented</Badge> : (c.idleDays ?? '—')}
                                            </TableCell>
                                            <TableCell><Icon glyph={open[c.plate] ? ICONS.chevronUp : ICONS.chevronDown} size="sm" /></TableCell>
                                        </TableRow>
                                        {open[c.plate] && (
                                            <TableRow>
                                                <TableCell colSpan={11} className="bg-purple-50/40">
                                                    {c.periods.length ? (
                                                        <table className="w-full text-xs">
                                                            <thead><tr className="text-gray-600">{['Contract', 'Customer', 'Phone', 'Branch', 'From', 'To'].map((h) => <th key={h} className="py-1 px-2 text-center font-semibold">{h}</th>)}</tr></thead>
                                                            <tbody>
                                                                {c.periods.map((p, j) => (
                                                                    <tr key={j} className="border-t border-purple-100">
                                                                        <td className="py-1 px-2 text-center"><CellId>{p.contract}</CellId></td>
                                                                        <td className="py-1 px-2 text-center">{p.customer || '—'}</td>
                                                                        <td className="py-1 px-2 text-center ds-cell-phone">{phoneOf(p.contract) || '—'}</td>
                                                                        <td className="py-1 px-2 text-center">{p.branch || '—'}</td>
                                                                        <td className="py-1 px-2 text-center tabular-nums whitespace-nowrap">{when(p.start)}</td>
                                                                        <td className="py-1 px-2 text-center tabular-nums whitespace-nowrap">{p.isOpen ? <Badge size="sm" variant="info">Open</Badge> : when(p.end)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    ) : <span className="text-xs text-gray-600">No contract in this period{c.last ? ` - last one ${c.last.contract} (${c.last.customer}) ended ${when(c.last.end)}` : ''}.</span>}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </Fragment>
                                ))}
                                {report && !shown.length && (
                                    <TableRow><TableCell colSpan={11}><EmptyState icon={ICONS.fleet} title={search || view !== 'all' ? 'No car matches.' : 'No cars for this branch.'} /></TableCell></TableRow>
                                )}
                                {!report && busy && (
                                    <TableRow><TableCell colSpan={11}><div className="py-6"><LoadingSpinner message={busy} /></div></TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <div className="text-xs text-gray-600 mt-2">
                        Click a car for who had it when · usage = days rented ÷ days of the period (up to now) · idle days = since the last contract ended · click a contract number for Ejar / The Engine / Customer profile.
                    </div>
                </div>
            </div>
        </Layout>
    );
}
